import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/ui/icon.js';
import { Button } from '../components/ui/button.js';
import { GameCard, type GameLike } from '../components/lobby/GameCard.js';
import { ORIGINALS } from '../features/originals/catalog.js';
import { originalName } from '../features/originals/names.js';
import { useOriginalsCatalog, withOriginalsCatalog } from '../features/originals/useOriginalsCatalog.js';
import { SiteFooter } from '../components/site/SiteFooter.js';

export const Route = createFileRoute('/_player/games/originals/')({
  component: OriginalsPage,
});

// Same canonical source as the home wheel, navbar dropdown and casino row.
const ORIGINALS_CARDS: GameLike[] = ORIGINALS.map((o) => ({
  id: `originals:${o.code}`,
  name: o.name,
  code: o.code,
  gameType: o.code,
  providerDisplayName: 'Originals',
}));

function OriginalsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const originalsCatalog = useOriginalsCatalog();
  const cards = withOriginalsCatalog(
    ORIGINALS_CARDS.map((g) => ({ ...g, name: originalName(g.code ?? '', t) })),
    originalsCatalog,
  );

  // Open for a visitor too: the game page renders without a session and asks
  // for a sign-in only at the bet.
  function onPlay(code: string) {
    void navigate({ to: '/games/originals/$code', params: { code } });
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1180px] mx-auto">
      <div className="flex items-center gap-2.5">
        <Icon name="star" size={24} className="text-[var(--accent-strong)]" />
        <h1 className="bf-display text-[28px] md:text-[40px] leading-none text-[var(--text-primary)] drop-shadow-[0_2px_10px_var(--primary-glow)]">
          {t('nav.originals')}
        </h1>
        <span className="font-mono text-[12px] text-[var(--text-muted)]">{ORIGINALS_CARDS.length}</span>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/casino' })} className="ml-auto">
          <Icon name="arrowRight" size={15} className="rotate-180" />
          {t('nav.casino')}
        </Button>
      </div>

      <div className="-mt-2 flex max-w-2xl flex-col gap-1.5">
        <p className="text-[15px] md:text-base font-semibold text-[var(--text-primary)]">
          {t('intro.title', { ns: 'originals' })}
        </p>
        <p className="text-[13.5px] md:text-sm leading-relaxed text-[var(--text-secondary)]">
          {t('intro.body', { ns: 'originals' })}
        </p>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
        {cards.map((g) => (
          <GameCard key={g.id} game={g} onPlay={() => onPlay(g.code ?? '')} />
        ))}
      </div>

      <SiteFooter />
    </div>
  );
}
